import {
  EnvironmentProviders,
  Provider,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, firstValueFrom, forkJoin, map, of } from 'rxjs';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LANGUAGE_SERVICE } from 'continity-shared-ui';

import { ShellLanguageService } from './shell-language.service';
import { ShellThemeService } from './shell-theme.service';

/** Options for {@link provideShell}. */
export interface ShellOptions {
  /** i18n loader prefix, e.g. './assets/i18n/'. Required when no translateLoaderFactory. */
  i18nPrefix?: string;
  /** i18n loader suffix, e.g. '/phoenix.json'. Required when no translateLoaderFactory. */
  i18nSuffix?: string;
  /**
   * Several i18n bundles per language, merged into ONE translation object (later entries win on
   * key collisions), e.g. `['/common.json', '/phoenix.json']`.
   *
   * Use this whenever the app renders shared-ui components: those emit their OWN keys under the
   * `common.*` namespace (`common.table.*`, `common.pagination.*`, …), which live in `common.json`
   * — NOT in an app-specific bundle. An app that loads only its own bundle renders those keys RAW
   * (the `common.pagination.pageOf` class of bug), and no test catches it, because app-level i18n
   * parity checks only scan the app's own key prefix.
   *
   * Takes precedence over {@link i18nSuffix}. Mutually exclusive with translateLoaderFactory.
   */
  i18nSuffixes?: string[];
  /** Default ngx-translate language, e.g. 'en'. */
  defaultLanguage: string;
  /** Custom ngx-translate loader factory (e.g. the main portal's multi-bundle loader). */
  translateLoaderFactory?: (http: HttpClient) => TranslateLoader;
}

/** Plain nested translation object as parsed from an i18n bundle. */
type TranslationTree = Record<string, unknown>;

/**
 * Recursively merges `source` INTO `target` (mutating it) — object nodes are merged, every other
 * value (string/array/null) is overwritten. Mirrors the main portal's loader semantics: with the
 * bundles applied in order, a later bundle wins on a colliding leaf.
 */
function deepMerge(target: TranslationTree, source: TranslationTree): TranslationTree {
  for (const key of Object.keys(source)) {
    const incoming = source[key];
    const existing = target[key];
    const bothPlainObjects =
      incoming !== null &&
      typeof incoming === 'object' &&
      !Array.isArray(incoming) &&
      existing !== null &&
      typeof existing === 'object' &&
      !Array.isArray(existing);
    target[key] = bothPlainObjects
      ? deepMerge(existing as TranslationTree, incoming as TranslationTree)
      : incoming;
  }
  return target;
}

/**
 * ngx-translate loader that fetches SEVERAL bundles per language and deep-merges them in order.
 *
 * Fetch failures are contained per bundle: a missing/broken bundle degrades to `{}` (its keys
 * render raw) instead of rejecting the whole forkJoin, which would leave the app with NO
 * translations at all. That trade matters here because the bundles can come from different asset
 * roots — one mis-copied file must not blank the entire UI. Every failure is logged.
 */
export class MergingTranslateHttpLoader implements TranslateLoader {
  constructor(
    private readonly http: HttpClient,
    private readonly prefix: string,
    private readonly suffixes: string[]
  ) {}

  getTranslation(lang: string): Observable<TranslationTree> {
    const requests = this.suffixes.map((suffix) => {
      const url = `${this.prefix}${lang}${suffix}`;
      return this.http.get<TranslationTree>(url).pipe(
        catchError((err: unknown) => {
          console.error(
            `[shell-i18n] Failed to load translation bundle "${url}" — its keys will render raw.`,
            err
          );
          return of({} as TranslationTree);
        })
      );
    });
    return forkJoin(requests).pipe(
      map((bundles) => bundles.reduce<TranslationTree>((acc, b) => deepMerge(acc, b), {}))
    );
  }
}

/**
 * Wires the VISUAL shell providers (frame only — ADO #1607/#1608):
 *  - ngx-translate TranslateModule.forRoot (HTTP loader + default language),
 *  - the LANGUAGE_SERVICE binding (shared-ui app-language-selector),
 *  - an app initializer that eagerly constructs the theme service (pre-paint dark-mode)
 *    and restores the user's saved UI language.
 *
 * AUTH/SSO + the HTTP client + runtime config now live in `provideAuth()`
 * (continity-shared-auth). provideShell relies on the HttpClient provided by provideAuth
 * for its TranslateHttpLoader, so call provideAuth() (or provideHttpClient) ALONGSIDE
 * provideShell(). Router + animations stay in the app because routes are app-specific.
 */
export function provideShell(
  options: ShellOptions
): (Provider | EnvironmentProviders)[] {
  const httpLoaderFactory = (http: HttpClient): TranslateLoader => {
    if (options.translateLoaderFactory) {
      return options.translateLoaderFactory(http);
    }
    if (options.i18nPrefix == null) {
      throw new Error(
        'provideShell: i18nPrefix is required when no translateLoaderFactory is supplied.'
      );
    }
    // Several bundles per language (e.g. shared-ui's common.json + the app's own) — merged in
    // order, later wins. Takes precedence over the single-suffix form.
    if (options.i18nSuffixes?.length) {
      return new MergingTranslateHttpLoader(http, options.i18nPrefix, options.i18nSuffixes);
    }
    if (options.i18nSuffix == null) {
      throw new Error(
        'provideShell: i18nSuffix or i18nSuffixes is required when no translateLoaderFactory is supplied.'
      );
    }
    return new TranslateHttpLoader(http, options.i18nPrefix, options.i18nSuffix);
  };

  return [
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: options.defaultLanguage,
      })
    ),
    { provide: LANGUAGE_SERVICE, useExisting: ShellLanguageService },
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      // Construct the theme service eagerly so the persisted/OS dark-mode choice is
      // applied to <html> before the app's first paint (avoids a light->dark flash).
      inject(ShellThemeService);
      const saved = localStorage.getItem('app_language');
      await firstValueFrom(translate.use(saved || options.defaultLanguage));
    }),
  ];
}
